import * as React from 'react'
import * as cn from 'classnames'

import * as styles from './IntermissionHeroCard.module.scss'
import { Info } from "@amzn/dvui/dist/Icons/Info"
import { Text } from '@amzn/dvui/dist/Text'
import { scaleImageUrl } from '../../../../utils/image';
import { StorefrontItem, WatchlistAction, ItemPrerolls, withArgs, Args } from '@utils';
import DraperPlayer from '../../../../../../DVWebNodeDraperPlayer/DraperPlayer';
import { VideoContextType } from '../../../../../../DVWebNodeDraperPlayer/videoContext';
import { DraperPlayerPlacement } from '../../../../../../DVWebNodeDraperPlayer/types'
import { PlayButton }  from '../Buttons/PlayButton';
import { CircularButton } from '../Buttons/CircularButton';
import { WatchlistButton } from '../Buttons/WatchlistButton';
import { MaturityRating } from '@amzn/dvui/dist/MaturityRating'
import { ReviewsRating } from './ReviewsRating'

export interface IntermissionHeroCardProps extends StorefrontItem {
    prerolls: ItemPrerolls // Making this mandatory here
    className: string
    isAnimating: boolean
    inView: boolean
    videoContext: VideoContextType
    updateWatchlistAction?: (newWatchlistAction: WatchlistAction) => void
    isLastItem: boolean
    moveToNextItem: () => void
}

class IntermissionHeroCardComponent extends React.PureComponent<IntermissionHeroCardProps & Args> {
    player?: DraperPlayer

    onInViewChange() {
        if (this.props.inView) {
            this.player && this.player.startPlayer()
        } else {
            !this.props.videoContext.fullScreen && this.player && this.player.stop()
        }
    }

    componentDidUpdate(prevProps: IntermissionHeroCardProps) {
        if (prevProps.titleID !== this.props.titleID) {
            this.player && this.player.startPlayer()
        }

        if (prevProps.inView !== this.props.inView) {
            this.onInViewChange()
        }
    }

    getBackgroundCss(): React.CSSProperties {
        const gradientDirection = (this.props.render && this.props.render.isRTL) ? '90deg' : '270deg'
        if (this.props.image.url && !this.props.videoContext.fullScreen) {
            const blurredImage = scaleImageUrl(this.props.image.url, 1500, 'JPG', 40)
            return { backgroundImage: `linear-gradient(${gradientDirection},rgba(37,46,57,0%),#252e39 80%), url("${blurredImage}")` }
        } else {
            return {}
        }
    }
    
    render() {
        const draperPlayerParentListeners = {
            onPlayComplete: () => {
                if (this.props.isLastItem) {
                    this.player && this.player.updateFullscreen(false)
                } else {
                    this.props.moveToNextItem()
                }
            }
        }

        return (    
            <div className={cn(styles.container, this.props.className, {
                [styles.fullScreen]: this.props.videoContext.fullScreen,
                [styles.animating]: this.props.isAnimating
            })}>
                <div className={styles.data} style={this.getBackgroundCss()}>
                    <a className={styles.titleBlock} href={this.props.link.url}>
                        <h3 className={styles.title}>{this.props.title}</h3>
                    </a>

                    <div className={styles.bottomBlock}>
                        <div className={styles.buttons}>
                            {this.props.playbackAction && 
                                <PlayButton
                                    playbackAction={this.props.playbackAction}
                                    prerolls={this.props.prerolls}
                                    stopDraperPlayer={() => {
                                        if (!this.player) return
                                        this.player.stop()
                                        this.player.updateFullscreen(false)
                                    }}
                                />}
                            {this.props.watchlistAction && <WatchlistButton
                                watchlistAction={this.props.watchlistAction}
                                prerolls={this.props.prerolls}
                                updateWatchlistAction={this.props.updateWatchlistAction}
                            />}
                            {this.props.link && this.props.link.url &&
                                <CircularButton
                                    href={this.props.link.url}
                                    icon={<Info />}
                                />
                            }
                        </div>
                        <div className={styles.metadata}>
                            {this.props.customerReviews && this.props.customerReviews.value && <ReviewsRating
                                rating={this.props.customerReviews.value}
                                link={this.props.customerReviews.link}
                            />}
                            {this.props.contentMaturityRating && <MaturityRating {...this.props.contentMaturityRating} className={styles.maturityRating}>
                                {this.props.contentMaturityRating.link &&
                                    <Text>{this.props.contentMaturityRating.link}</Text>}
                            </MaturityRating>}
                            <Text className={styles.genres}>{this.props.genres}</Text>
                        </div>
                    </div>
                </div>
                <div className={styles.heroSection}>
                    {this.props.image && <img className={styles.packshot}
                        src={this.props.image.url}
                    />}
                    {this.props.prerolls && <DraperPlayer
                        prerolls={this.props.prerolls}
                        placement={DraperPlayerPlacement.IntermissionHero}
                        videoContext={this.props.videoContext}
                        playerIsActive={false}
                        enableFullscreen={true}
                        parentListeners={draperPlayerParentListeners}
                        link={this.props.link && this.props.link.url}
                        ref={(ref: DraperPlayer) => this.player = ref}
                    />}
                </div>
            </div>
        )
    }
}

export const IntermissionHeroCard = withArgs(IntermissionHeroCardComponent) as React.ComponentType<IntermissionHeroCardProps>